import axios, { AxiosInstance } from 'axios';
import {
  AzureAuthResponse,
  AuthUrlResponse,
  User,
  Dataset,
  AnalysisRequest,
  AnalysisJob,
  AnalysisResults,
  Report,
  GeographicAccessResponse,
  JobShare,
  ShareRecipients,
} from '../types';
import {
  ScheduledAnalysis,
  ScheduledAnalysisCreate,
  ScheduledAnalysisUpdate
} from '../types/scheduled';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // Always use the environment variable if set, otherwise use relative URL
  return process.env.REACT_APP_API_URL || '/api';
};

const API_BASE_URL = getApiBaseUrl();

function authRefreshUrl(): string {
  const base = API_BASE_URL.replace(/\/$/, '');
  if (base.startsWith('http')) {
    return `${base}/auth/refresh`;
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = base.startsWith('/') ? base : `/${base}`;
  return `${origin}${path}/auth/refresh`;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 120000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    let refreshInFlight: Promise<string> | null = null;

    // Request interceptor - add authentication token from localStorage
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (config.headers && token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Development mode: Add X-Dev-User header for RBAC testing
        if (process.env.NODE_ENV === 'development') {
          const devUser = localStorage.getItem('dev_user');
          if (devUser && config.headers) {
            config.headers['X-Dev-User'] = devUser;
          }
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const originalRequest = error.config as typeof error.config & {
          _authRetry?: boolean;
        };
        if (status !== 401 || !originalRequest) {
          return Promise.reject(error);
        }

        const reqUrl = String(originalRequest.url ?? '');
        const onLoginFlow =
          typeof window !== 'undefined' &&
          (window.location.pathname === '/auth/login' ||
            window.location.pathname.startsWith('/auth/callback'));
        const isAuthBootstrap =
          reqUrl.includes('/auth/refresh') ||
          reqUrl.includes('/auth/login') ||
          reqUrl.includes('/auth/callback');

        const sendToLogin = () => {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expires_at');
          if (
            typeof window !== 'undefined' &&
            window.location.pathname !== '/auth/login' &&
            !window.location.pathname.startsWith('/auth/callback')
          ) {
            window.location.href = '/auth/login';
          }
        };

        if (isAuthBootstrap) {
          if (!onLoginFlow) {
            sendToLogin();
          }
          return Promise.reject(error);
        }

        if (originalRequest._authRetry) {
          sendToLogin();
          return Promise.reject(error);
        }

        const rt = localStorage.getItem('refresh_token');
        if (!rt) {
          sendToLogin();
          return Promise.reject(error);
        }

        originalRequest._authRetry = true;

        try {
          if (!refreshInFlight) {
            refreshInFlight = (async () => {
              const { data } = await axios.post<{
                access_token: string;
                refresh_token?: string;
                expires_in?: number;
              }>(authRefreshUrl(), null, {
                params: { refresh_token: rt },
                timeout: 30000,
              });
              localStorage.setItem('access_token', data.access_token);
              if (data.refresh_token) {
                localStorage.setItem('refresh_token', data.refresh_token);
              }
              if (data.expires_in) {
                localStorage.setItem(
                  'token_expires_at',
                  String(Date.now() + data.expires_in * 1000)
                );
              }
              return data.access_token;
            })().finally(() => {
              refreshInFlight = null;
            });
          }
          const access = await refreshInFlight;
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return this.client.request(originalRequest);
        } catch {
          sendToLogin();
          return Promise.reject(error);
        }
      }
    );
  }

  async getArcgisCourses(): Promise<Record<string, string[]>> {
    const response = await this.client.get<Record<string, string[]>>(`/arcgis/courses`);
    return response.data;
  }

  async getArcgisStudents(courseId?: string): Promise<string[]> {
    const response = await this.client.get<string[]>(`/arcgis/students`, {
      params: {
        ...(courseId ? { course_id: courseId } : {}),
        include_both_types: true,
        use_lookup: true
      },
      timeout: 20000,
    });
    return response.data;
  }

  async getCourseDateRanges(): Promise<Record<string, { min_date: string; max_date: string; point_count: number }>> {
    const response = await this.client.get(`/pathfinder-postgres/course-date-ranges`);
    return response.data;
  }

  // Azure AD Authentication
  async getAuthUrl(): Promise<AuthUrlResponse> {
    const response = await this.client.get<AuthUrlResponse>('/auth/login');
    return response.data;
  }

  async handleAuthCallback(code: string): Promise<AzureAuthResponse> {
    const response = await this.client.get<AzureAuthResponse>(`/auth/callback?code=${code}`);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  // Geographic Access Control
  async getGeographicAccess(): Promise<GeographicAccessResponse> {
    const response = await this.client.get<GeographicAccessResponse>('/auth/geographic-access');
    return response.data;
  }

  async checkPermission(requiredRole: string): Promise<{
    has_permission: boolean;
    user_roles: string[];
    required_role: string;
    user_level: number;
    required_level: number;
  }> {
    const response = await this.client.get(`/auth/check-permission/${requiredRole}`);
    return response.data;
  }

  //remove?
  // Dataset Management
  async getDatasets(): Promise<Dataset[]> {
    const response = await this.client.get<Dataset[]>(`/upload/datasets`);
    return response.data;
  }

  // Analysis Jobs
  async startAnalysis(request: AnalysisRequest): Promise<AnalysisJob> {
    const response = await this.client.post<AnalysisJob>('/analysis/start', request);
    return response.data;
  }

  async getAnalysisJobs(
    page: number = 0,
    limit: number = 12,
    search?: string,
    scope: 'own' | 'shared' = 'own'
  ): Promise<{
    jobs: AnalysisJob[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    const params: Record<string, any> = { page, limit, scope };
    if (search) params.search = search;

    const response = await this.client.get('/analysis/jobs', { params });
    return response.data;
  }

  // --- Job sharing ---

  async getJobShares(jobId: number): Promise<JobShare[]> {
    const response = await this.client.get<JobShare[]>(`/analysis/jobs/${jobId}/shares`);
    return response.data;
  }

  async shareJob(jobId: number, upns: string[], orgIds: number[]): Promise<JobShare[]> {
    const response = await this.client.post<JobShare[]>(`/analysis/jobs/${jobId}/shares`, {
      upns,
      org_ids: orgIds,
    });
    return response.data;
  }

  async revokeJobShare(jobId: number, target: { upn?: string; org_id?: number }): Promise<void> {
    // Axios needs the body under `data` for DELETE requests
    await this.client.delete(`/analysis/jobs/${jobId}/shares`, { data: target });
  }

  async getShareRecipients(): Promise<ShareRecipients> {
    const response = await this.client.get<ShareRecipients>('/analysis/sharing/recipients');
    return response.data;
  }

  async getAnalysisJob(jobId: number): Promise<AnalysisJob> {
    const response = await this.client.get<AnalysisJob>(`/analysis/jobs/${jobId}`);
    return response.data;
  }

  async getAllAnalysisJobs(): Promise<AnalysisJob[]> {
    // Fetch with large limit to get all jobs (including other users for Reports page)
    const response = await this.client.get('/analysis/jobs', {
      params: { page: 0, limit: 1000, all_users: true },
    });
    return response.data.jobs;
  }

  async downloadReport(jobId: number, format: 'html' | 'csv' | 'json'): Promise<any> {
    const response = await this.client.get(`/analysis/jobs/${jobId}/download`, {
      params: { format },
      responseType: format === 'json' ? 'json' : 'blob'
    });
    return response;
  }

  async regenerateAiInsights(jobId: number): Promise<{ success: boolean; message: string; insights_count?: number }> {
    const response = await this.client.post(`/analysis/jobs/${jobId}/regenerate-ai-insights`);
    return response.data;
  }

  async getAnalysisResults(jobId: number): Promise<AnalysisResults> {
    const response = await this.client.get<AnalysisResults>(`/analysis/jobs/${jobId}/results`);
    return response.data;
  }

  async deleteAnalysisJob(jobId: number): Promise<void> {
    await this.client.delete(`/analysis/jobs/${jobId}`);
  }

  async fixStuckJobs(): Promise<{ message: string; fixed_jobs: number[] }> {
    const response = await this.client.post('/analysis/jobs/fix-stuck');
    return response.data;
  }

  // Paginated endpoints
  async getDwellLocations(jobId: number, page: number = 0, limit: number = 20, search?: string, deviceType?: string): Promise<{
    items: any[],
    total: number,
    page: number,
    limit: number,
    pages: number
  }> {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    if (deviceType && deviceType !== 'all') params.device_type = deviceType;
    
    const response = await this.client.get(`/analysis/jobs/${jobId}/dwell-locations`, { params });
    return response.data;
  }

  async getRoutes(jobId: number, page: number = 0, limit: number = 20, search?: string, deviceType?: string): Promise<{
    items: any[],
    total: number,
    page: number,
    limit: number,
    pages: number
  }> {
    const params: Record<string, any> = { page, limit };
    if (search) params.search = search;
    if (deviceType && deviceType !== 'all') params.device_type = deviceType;
    
    const response = await this.client.get(`/analysis/jobs/${jobId}/routes`, { params });
    return response.data;
  }

  // Reports
  async getJobReports(jobId: number): Promise<Report[]> {
    const response = await this.client.get<Report[]>(`/reports/job/${jobId}`);
    return response.data;
  }

  async downloadAllReports(jobId: number): Promise<Blob> {
    const response = await this.client.get(`/reports/download-all/${jobId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Scheduled Analyses
  async getScheduledAnalyses(): Promise<ScheduledAnalysis[]> {
    const response = await this.client.get<ScheduledAnalysis[]>('/scheduled-analyses');
    return response.data;
  }

  async getScheduledAnalysis(scheduleId: number): Promise<ScheduledAnalysis> {
    const response = await this.client.get<ScheduledAnalysis>(`/scheduled-analyses/${scheduleId}`);
    return response.data;
  }

  async createScheduledAnalysis(data: ScheduledAnalysisCreate): Promise<ScheduledAnalysis> {
    const response = await this.client.post<ScheduledAnalysis>('/scheduled-analyses', data);
    return response.data;
  }

  async updateScheduledAnalysis(scheduleId: number, data: ScheduledAnalysisUpdate): Promise<ScheduledAnalysis> {
    const response = await this.client.patch<ScheduledAnalysis>(`/scheduled-analyses/${scheduleId}`, data);
    return response.data;
  }

  async toggleScheduledAnalysis(scheduleId: number, isActive: boolean): Promise<ScheduledAnalysis> {
    const response = await this.client.post<ScheduledAnalysis>(`/scheduled-analyses/${scheduleId}/toggle`, {
      is_active: isActive
    });
    return response.data;
  }

  async deleteScheduledAnalysis(scheduleId: number): Promise<void> {
    await this.client.delete(`/scheduled-analyses/${scheduleId}`);
  }

  async runScheduledAnalysisNow(scheduleId: number): Promise<{ message: string; job_id: number; schedule_id: number }> {
    const response = await this.client.post(`/scheduled-analyses/${scheduleId}/run-now`);
    return response.data;
  }

  // Digital Pattern of Life
  async getDigitalPoL(params: {
    course_id?: string;
    entity_ids?: string;
    days?: number;
    activity_type?: string;
    limit?: number;
  }): Promise<any[]> {
    const response = await this.client.get('/pathfinder/digital-pol', { params });
    return response.data;
  }

  // User Preferences
  async getUserPreferences(): Promise<any> {
    const response = await this.client.get('/preferences/');
    return response.data;
  }

  async saveUserPreferences(preferences: any): Promise<any> {
    const response = await this.client.post('/preferences/', preferences);
    return response.data;
  }

  async updateUserPreferences(preferences: any): Promise<any> {
    const response = await this.client.put('/preferences/', preferences);
    return response.data;
  }

  async deleteUserPreferences(): Promise<void> {
    await this.client.delete('/preferences/');
  }

  // Custom Color Schemes
  async getCustomColorSchemes(): Promise<any[]> {
    const response = await this.client.get('/custom-color-schemes/');
    return response.data;
  }

  async createCustomColorScheme(scheme: {
    scheme_name: string;
    description?: string;
    primary_color: string;
    secondary_color: string;
    accent_color?: string;
  }): Promise<any> {
    const response = await this.client.post('/custom-color-schemes/', scheme);
    return response.data;
  }

  async updateCustomColorScheme(schemeId: number, scheme: {
    scheme_name?: string;
    description?: string;
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
  }): Promise<any> {
    const response = await this.client.put(`/custom-color-schemes/${schemeId}`, scheme);
    return response.data;
  }

  async deleteCustomColorScheme(schemeId: number): Promise<void> {
    await this.client.delete(`/custom-color-schemes/${schemeId}`);
  }

  // Watchlist - Saved Devices
  async getWatchlist(courseId?: string, activeOnly: boolean = true): Promise<any[]> {
    const response = await this.client.get('/watchlist', {
      params: {
        ...(courseId ? { course_id: courseId } : {}),
        active_only: activeOnly
      }
    });
    return response.data;
  }

  async addToWatchlist(device: {
    registration_id: string;
    name?: string;
    notes?: string;
    tags?: string[];
    course_id?: string;
  }): Promise<any> {
    const response = await this.client.post('/watchlist', device);
    return response.data;
  }

  async removeFromWatchlist(id: number): Promise<void> {
    await this.client.delete(`/watchlist/${id}`);
  }

  // Geofences
  async getGeofences(courseId?: string, activeOnly: boolean = true): Promise<any[]> {
    const response = await this.client.get('/geofences', {
      params: {
        ...(courseId ? { course_id: courseId } : {}),
        active_only: activeOnly
      }
    });
    return response.data;
  }

  async createGeofence(geofence: {
    name: string;
    description?: string;
    geometry: any;
    course_id?: string;
  }): Promise<any> {
    const response = await this.client.post('/geofences', geofence);
    return response.data;
  }

  async deleteGeofence(id: number): Promise<void> {
    await this.client.delete(`/geofences/${id}`);
  }

  // Administration
  async getMyAccess(): Promise<UserAccessInfo> {
    const response = await this.client.get<UserAccessInfo>('/admin/my-access');
    return response.data;
  }

  async getAdminConfig(): Promise<AdminConfigResponse> {
    const response = await this.client.get<AdminConfigResponse>('/admin/config');
    return response.data;
  }

  async getAzurePortalUrls(): Promise<AzurePortalUrls> {
    const response = await this.client.get<AzurePortalUrls>('/admin/azure-portal-url');
    return response.data;
  }

  // User Management
  async listUsers(search?: string, top: number = 100): Promise<UserListResponse> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('top', top.toString());
    const response = await this.client.get<UserListResponse>(`/admin/users?${params.toString()}`);
    return response.data;
  }

  async getUser(userId: string): Promise<AzureADUser> {
    const response = await this.client.get<AzureADUser>(`/admin/users/${userId}`);
    return response.data;
  }

  async listAzureGroups(): Promise<GroupListResponse> {
    const response = await this.client.get<GroupListResponse>('/admin/groups');
    return response.data;
  }

  async getGroupMembers(groupId: string): Promise<AzureADUser[]> {
    const response = await this.client.get<AzureADUser[]>(`/admin/groups/${groupId}/members`);
    return response.data;
  }

  async addUserToGroup(groupId: string, userId: string): Promise<void> {
    await this.client.post(`/admin/groups/${groupId}/members/${userId}`);
  }

  async removeUserFromGroup(groupId: string, userId: string): Promise<void> {
    await this.client.delete(`/admin/groups/${groupId}/members/${userId}`);
  }

  async updateUserGroups(userId: string, groupIds: string[]): Promise<any> {
    const response = await this.client.put(`/admin/users/${userId}/groups`, {
      user_id: userId,
      group_ids: groupIds
    });
    return response.data;
  }

  async createTracerGroup(displayName: string, description?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('display_name', displayName);
    if (description) params.append('description', description);
    const response = await this.client.post(`/admin/groups?${params.toString()}`);
    return response.data;
  }

  // ==================== Intelligence Enrichment ====================

  async getEnrichmentStatus(): Promise<EnrichmentStatusResponse> {
    const response = await this.client.get('/enrichment/status');
    return response.data;
  }

  async enrichLocation(
    latitude: number,
    longitude: number,
    locationName?: string,
    context?: string
  ): Promise<EnrichmentResponse> {
    const response = await this.client.post('/enrichment/location', {
      latitude,
      longitude,
      location_name: locationName,
      context
    });
    return response.data;
  }

  async enrichEntity(
    entityName: string,
    entityType?: string,
    context?: string
  ): Promise<EnrichmentResponse> {
    const response = await this.client.post('/enrichment/entity', {
      entity_name: entityName,
      entity_type: entityType,
      context
    });
    return response.data;
  }

  async enrichPattern(
    patternType: string,
    locationName?: string,
    details?: string
  ): Promise<EnrichmentResponse> {
    const response = await this.client.post('/enrichment/pattern', {
      pattern_type: patternType,
      location_name: locationName,
      details
    });
    return response.data;
  }

  async enrichSearch(
    query: string,
    searchDepth: 'basic' | 'advanced' = 'basic',
    maxResults: number = 5
  ): Promise<EnrichmentResponse> {
    const response = await this.client.post('/enrichment/search', {
      query,
      search_depth: searchDepth,
      max_results: maxResults
    });
    return response.data;
  }

  async autoEnrichNote(noteId: number): Promise<AutoEnrichmentResult> {
    const response = await this.client.post(`/enrichment/auto-enrich-note/${noteId}`);
    return response.data;
  }

}

// Administration types
export interface RegionInfo {
  name: string;
  description?: string;
  bbox?: number[];
  country_codes?: string[];
}

export interface GroupAccessInfo {
  group_name: string;
  description?: string;
  worldwide_access: boolean;
  allowed_regions: string[];
  max_query_radius_meters: number;
  max_date_range_days: number;
}

export interface UserAccessInfo {
  username: string;
  email: string;
  name: string;
  groups: string[];
  roles: string[];
  geographic_access?: GroupAccessInfo;
  allowed_regions: RegionInfo[];
  is_admin: boolean;
}

export interface AdminConfigResponse {
  current_user: UserAccessInfo;
  all_groups: GroupAccessInfo[];
  all_regions: RegionInfo[];
  gac_enabled: boolean;
}

export interface AzurePortalUrls {
  is_admin: boolean;
  azure_ad_users_url: string;
  azure_ad_groups_url: string;
  enterprise_app_url: string;
  tenant_id: string;
}

export interface AzureADUser {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  accountEnabled: boolean;
  groups: string[];
}

export interface AzureADGroup {
  id: string;
  displayName: string;
  description?: string;
  memberCount: number;
}

export interface UserListResponse {
  users: AzureADUser[];
  total: number;
}

export interface GroupListResponse {
  groups: AzureADGroup[];
}

// Intelligence Enrichment types
export interface EnrichmentSource {
  title: string;
  url: string;
  snippet: string;
}

export interface EnrichmentResponse {
  success: boolean;
  query: string;
  summary: string;
  sources: EnrichmentSource[];
  error?: string;
  // Discovered location info from reverse geocoding
  discovered_place_name?: string;
  discovered_address?: string;
  discovered_place_type?: string;
  discovered_city?: string;
  discovered_country?: string;
}

export interface EnrichmentStatusResponse {
  enabled: boolean;
  message: string;
}

export interface AutoEnrichmentResult {
  note_id: number;
  enrichments: Array<{
    type: string;
    coordinates?: string;
    name?: string;
    summary: string;
    sources: EnrichmentSource[];
  }>;
  count: number;
}

export const apiService = new ApiService();
export default apiService;
